// packages/web/src/screens/YourPage.tsx — B8: the performer's public page,
// preview through publish through the live link.
//
// Brief: docs/beta-door/B8-your-page.md. Blessed design (Huan, 2026-09-24,
// decision `defa9aed`): mock-up https://claude.ai/artifact/XTYhCVTH8mVd4N5XK3Txs5,
// five phone screens — Preview, Address taken, Before it goes live, Live,
// Not yet. Its copy is used verbatim EXCEPT where the server's own sentence
// wins over the mock's stand-in (the consent paragraphs, the typed-name
// prompt, and every refusal) — the non-negotiable both F0 and this brief
// name explicitly.
//
// Reached from two places, carried in `mode`:
//   - 'onboarding' — the end of first-run (Onboarding.tsx's `continueToPage`
//     seam). The onward button says "Go to Today" and finishes setup.
//   - 'more' — More → Your page, any time later. The onward button says
//     "Done"; once published this screen also offers the unpublish control
//     (F5's own `DELETE .../page`) that first-run never shows.
//
// ⚑⚑ THE SERVER SIDE IS FULLY BUILT (F5/P1/P3/B6) — this is its first
// client. Every route is session + membership + tier gated and reads the
// performer's `aa_subject` id, which this screen resolves the same way
// PerformerResume.tsx already does: "an account is one career" (2026-09-19
// ruling), so `listHouseholdPerformers`'s first row IS the performer, full
// stop — there is never a second one to choose between.
//
// ⚑⚑ THE MINOR GATE THIS CHUNK ACTUALLY BUILDS. F5 built a full minor-
// publish flow (typed confirmation, guardian check, guardian notification) —
// but B8's own brief defers wiring that UI: "Minors skip it in first-run…
// More → Your page shows the server's own refusal sentence for them rather
// than a publish button. (The guardian flow is a later chunk.)" So this
// screen gates on ONE question: is this account a CONFIRMED adult? That is
// exactly `consent-text`'s own `isMinor` field (`age.isMinor !== false` —
// core/publicPage/pages.ts), which reads a real minor and an unestablished
// age the same protective way. `isMinor === true` here therefore covers two
// server-side cases:
//   - age unresolved (no attestation, or an explicit "under 18" attestation
//     with no real birth date) — B6's own `age_unknown` refusal.
//   - a real, DOB-confirmed minor — F5's guardian flow, genuinely deferred.
// Both used to read the same honest way here: no publish button, a plain
// sentence, hand-typed as a local copy the file's own comment flagged as a
// drift risk (B9) because no GET route exposed the server's sentence
// without an unsafe probe-publish.
//
// ⚑⚑ S1 (decision 4691c16d, 2026-11-01) — the server now reads the
// PERFORMER's age band (`band`), not the signed-in holder's, and lets the
// holder publish for a minor; there is no guardian flow any more. This
// screen still publishes for adults only: a minor reads `minorMessage`, and
// an age that is answered but still unknown reads `ageUnknownMessage`, which
// names the performer's month and year of birth as the fix (R5).
//
// ⚑⚑ B12 — THE WALK FOUND BOTH DOORS THAT SENTENCE NAMED WERE FALSE
// (docs/beta-door/B12-your-page-asks-the-age.md). "Answer it in setup or on
// their profile" — setup asks the age question exactly once, during
// first-run, and no profile screen asks it at all, so an account created
// before B1's age step (09-24) was stuck behind that refusal forever. Two
// fixes, together:
//   1. The drift risk closed: `consent-text` now serves the sentence itself
//      (`ageUnknownMessage`, `@vowric/core`'s own `AGE_UNKNOWN_MESSAGE` —
//      the exact string `publishSubjectPage` throws), so this screen reads
//      it rather than keeping a second copy, and it no longer names either
//      door.
//   2. THE GENUINELY UNRESOLVED CASE gets a THIRD phase, `askAge`, instead
//      of the flat refusal: `consent-text` also serves `ageUnanswered` —
//      true only when nobody has EVER answered (no attestation of either
//      value, no real DOB) — and this screen offers the same question
//      Onboarding.tsx's own setup-age step asks, through the SAME shared
//      component (`components/AgeQuestion.tsx`), so the two can never drift
//      apart on wording. Offered ONLY when the signed-in session IS this
//      performer (`HouseholdPerformer.isSelf`,
//      `packages/server/src/plugins/household.ts` — `recordAgeAttestation`
//      writes to the SIGNED-IN user, so asking on someone else's behalf
//      would silently record the wrong person's answer). A real,
//      DOB-confirmed minor, and an account that already answered "No,
//      under 18" (still `age_unknown` — B6 §1: an is_adult=false
//      attestation does not manufacture a minor refusal — but `ageUnanswered`
//      is false once it exists), both keep reading the plain sentence, no
//      button: exactly what B8/B9 already did, now for a narrower set of
//      cases.
//
// ⚑⚑ B9 — three fixes from a real-browser walk of the live box:
//   1. The preview is now a real `<iframe src>` navigation to
//      `.../page/preview.html` (server: actorSubjectPage.ts), never
//      `srcdoc` — `srcdoc` INHERITS the app shell's CSP and blocked this
//      page's own inline `<style>`, invisible to jsdom (this file's own
//      `SubjectPagePreviewApi.html` comment in lib/api.ts carries the full
//      story). `sandbox="allow-same-origin"`, never `allow-scripts` — the
//      page has no script and must never get one; `allow-same-origin` is
//      needed only so the session cookie rides and this account's own media
//      loads.
//   2. Opening this screen no longer PUTs a slug. B8 auto-saved the
//      suggestion on load so the (now-superseded) JSON preview route would
//      not 409 `slug_required` — which meant every new account reserved its
//      name just by looking (the phone walk's finding #3). The new preview
//      route needs no address at all, so the suggestion is shown, unsaved,
//      and `attemptSaveAddress` (already PUT-then-confirm) is the only path
//      that ever writes `subject_pages.slug`.
//   3. The "On the page" strip: every headshot this account has, ticked when
//      it is a `subject_page_items` row; ticking/unticking calls
//      POST/DELETE .../page/items and bumps `previewNonce` so the iframe
//      reloads against the change. THIS READS `kind: 'headshot'` MEDIA — and
//      that is now correct for every source: since `bcc1db739`, BOTH setup's
//      "Your headshots" stop (Onboarding.tsx) AND Materials → Headshots
//      (MaterialsHeadshots.tsx) save `aa_media.fields.kind: 'headshot'`, which
//      is exactly what the page's own selection guard (`assertSelectable`,
//      core/publicPage/pages.ts) requires. A record uploaded BEFORE
//      `bcc1db739` still carries the old `kind: 'photo'` and does not appear
//      here or in the strip — not a bug, the record's own kind is simply
//      older than the rule; there is no migration that rewrites it.
//
// ⚑⚑ B11 — THE STRIP READ THE WRONG RECORD TYPE, SILENTLY (Atlas, a live-box
// walk 2026-09-25). This screen used to fetch the strip's photos with
// `listMedia(tenantId, 'headshot')` — `GET /api/actor/media`, the RETIRED
// Actor pack's own door onto `actor_media` (`actorMedia.ts`'s header: "the
// JULY ACTOR pack's namespace"). `actor_assist` deployments never seed that
// type at all (`boot({ packs: [resolvePack()] })` registers ONE pack), so
// every headshot ever filed as `aa_media` — which is every headshot this
// pack has ever produced — was invisible to the strip on the live box,
// silently: the read simply came back empty rather than erroring in an
// obvious way. Fixed by reading the SAME door MaterialsPhotos.tsx already
// reads correctly (`listAllRecordsByType` + `activeOnly` + a `kind` filter,
// this screen's own `loadMedia` below), which is also where the "Reels"
// group's own media comes from — one `aa_media` read, filtered two ways.
// `listMedia`/`/api/actor/media` must never be used on this pack again; see
// `feedback_grep_sweep_is_not_an_inventory` for why a route's NAME is not
// proof it is the right door.
//
// ⚑ ALSO FIXED: `loadStrip` used to `Promise.all([<media>, <items>])`, so a
// media-read failure took the ITEMS refresh down with it — a credit tick
// (which touches neither photos nor reels) could silently fail to render as
// ticked whenever the UNRELATED media read also failed. The two are now
// independent (`Promise.allSettled`, `loadStrip` below): `items` updates on
// its own success, `loadMedia` reports its own failure on its own, and a
// failed media read is no longer indistinguishable from "no headshots or
// reels yet" — it shows the server's own sentence instead of the section
// just not being there (P1's "absence is absent" is about nothing chosen,
// never about a read that failed).
//
// ⚑⚑ B11 §3 — REELS GET A TICK, AND CAN LEAD. A second grid beside the
// headshot strip, "Reels": each shown by its `cover_frame_ref` still (a
// plain "Reel" plate when one is absent — never a black rectangle, P2's own
// rule, extended here to this curation screen too), with the SAME tick
// door, `itemKind: 'reel'`. A reel uploaded from Materials → Reels is NOT
// pre-ticked (Atlas's own ruling, B11's brief §3 — unlike a headshot, which
// setup pre-ticks by a separate, earlier ruling). When at least one reel is
// ticked, "Headshot first" / "Reel first" offers the hero choice
// (`PUT .../page/appearance`); with none ticked the choice is simply absent
// (no dead option). Choosing "Reel first" while the page's current theme is
// the index (C) moves it to the title page (A) in the SAME write — migration
// 0109's own header: "promote the reel to hero and C has become A" — rather
// than leaving the artist to hit `index_theme_cannot_take_reel_hero` and
// wonder what to do about it.
//
// ⚑⚑ B10 §1 — CREDITS GET A TICK, LIKE HEADSHOTS. A second group under the
// strip, "Credits": one row per WRAPPED engagement (server:
// GET .../page/credit-candidates), each with a tick that POSTs/DELETEs the
// same `.../page/items` door the strip already uses, `itemKind: 'credit'`.
// Absent with zero candidates — no heading, no empty-state sentence (P1:
// absence is absent). Nothing is ticked by looking (B9 §2's rule, carried
// over). In-progress credits and the adult opt-in are OUT OF SCOPE here
// (B10 §1) — every candidate this screen ever shows is already wrapped.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  addSubjectPageItem,
  getSubjectPageConsentText,
  getSubjectPageCreditCandidates,
  getSubjectPageState,
  getSubjectPageSlugSuggestion,
  listAllRecordsByType,
  listHouseholdPerformers,
  publishSubjectPage,
  recordAgeAttestation,
  removeSubjectPageItem,
  setSubjectPageAppearance,
  setSubjectPageSlug,
  subjectPageCandidates,
  subjectPagePreviewUrl,
  unpublishSubjectPage,
  type RecordRow,
  type SubjectPageAppearanceApi,
  type SubjectPageConsentTextApi,
  type SubjectPageCreditCandidateApi,
  type SubjectPageHeroKind,
  type SubjectPageItemApi,
} from '../lib/api';
import { AA } from '../lib/auditionSpread';
import { activeOnly } from '../lib/retired';
import { RecordImage } from '../components/RecordMedia';
import { AgeQuestion, AGE_ANSWER_SAVE_FAILED_MESSAGE } from '../components/AgeQuestion';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './YourPage.css';

export interface YourPageProps {
  tenantId: string;
  mode: 'onboarding' | 'more';
  onDone: () => void;
}

type Phase =
  | 'loading'
  | 'error'
  | 'unavailable'
  | 'askAge'
  | 'preview'
  | 'taken'
  | 'confirm'
  | 'live'
  | 'notYet';

/** ⚑ THE ADDRESS PEOPLE SHARE (Huan, 2026-09-24: "match marketing"). The
 * server answers `/a/<slug>` as a RELATIVE path and the Live screen showed
 * exactly that — "/a/sam-walkthrough" — which is no link at all. The page is
 * served at the product's own apex (calltowrap.com passes `/a/*` through to
 * the app), so every shown, copied and shared address is built here, once. */
const PUBLIC_PAGE_ORIGIN = 'https://calltowrap.com';
const PAGE_ADDRESS_PREFIX = 'calltowrap.com/a/';

/** A server page URL (relative `/a/<slug>`) → the absolute address to share. */
export function sharedPageUrl(url: string): string {
  return /^https?:\/\//.test(url) ? url : `${PUBLIC_PAGE_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function YourPage({ tenantId, mode, onDone }: YourPageProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [subjectName, setSubjectName] = useState('');
  const [consent, setConsent] = useState<SubjectPageConsentTextApi | null>(null);
  // B9 §1 — the frame now navigates to a real URL; bumped after any item
  // change so the iframe reloads against the account's latest choices
  // (belt to the route's own `Cache-Control: no-store`).
  const [previewNonce, setPreviewNonce] = useState(0);

  // B9 §3 — "On the page": every headshot the account has, and which of
  // them are already `subject_page_items` rows. B11: the SAME `aa_media`
  // read also feeds the "Reels" group below (`loadMedia`) — one list,
  // filtered two ways, never two separate server reads for one table.
  const [photos, setPhotos] = useState<RecordRow[] | null>(null);
  const [reels, setReels] = useState<RecordRow[] | null>(null);
  const [items, setItems] = useState<SubjectPageItemApi[]>([]);
  const [stripError, setStripError] = useState<string | null>(null);
  const [stripBusyId, setStripBusyId] = useState<string | null>(null);
  const [reelBusyId, setReelBusyId] = useState<string | null>(null);

  // B11 §3 — the hero choice ("Headshot first" / "Reel first"), read from
  // GET .../page's own `appearance` (already built by P1/P3; this screen is
  // its first client) and written through `PUT .../page/appearance`.
  const [appearance, setAppearance] = useState<SubjectPageAppearanceApi | null>(null);
  const [appearanceBusy, setAppearanceBusy] = useState(false);
  const [appearanceError, setAppearanceError] = useState<string | null>(null);

  // B10 §1 — "Credits": every WRAPPED engagement this performer could tick,
  // and which of them already names a `subject_page_items` row (`items`,
  // above — the same list the headshot strip cross-references, since a
  // credit is just another item kind on it).
  const [creditCandidates, setCreditCandidates] = useState<SubjectPageCreditCandidateApi[] | null>(null);
  const [creditsError, setCreditsError] = useState<string | null>(null);
  const [creditBusyId, setCreditBusyId] = useState<string | null>(null);

  const [slugField, setSlugField] = useState('');
  const [takenLine, setTakenLine] = useState<string | null>(null);
  const [addrError, setAddrError] = useState<string | null>(null);
  const [unpublishReceipt, setUnpublishReceipt] = useState<string | null>(null);

  const [typedName, setTypedName] = useState('');
  const [publishError, setPublishError] = useState<string | null>(null);

  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const [busy, setBusy] = useState(false);

  // B12 — the in-place age question's own failed-save sentence, separate
  // from every other error slot on this screen (`addrError`, `publishError`,
  // …) so a failed answer here never bleeds into an unrelated phase.
  const [ageAnswerError, setAgeAnswerError] = useState<string | null>(null);

  /** B11 — the ONE `aa_media` read both the headshot strip AND the "Reels"
   * group filter from (this file's header carries the full account of why
   * it is `listAllRecordsByType` + `activeOnly` + a `kind` filter, never
   * `listMedia`/`/api/actor/media`). Never throws outward — a failed read
   * is the media sections' own, shown as `stripError` on whichever of them
   * would otherwise be empty, and never blocks the preview or the publish
   * flow (the strip and the reel group are a curation aid, not the page
   * itself). */
  async function loadMedia(): Promise<void> {
    try {
      const active = activeOnly(await listAllRecordsByType(tenantId, AA.MEDIA));
      setPhotos(active.filter((r) => r.fields['kind'] === 'headshot'));
      setReels(active.filter((r) => r.fields['kind'] === 'reel'));
      setStripError(null);
    } catch (err) {
      setStripError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'Your photos could not be read.');
    }
  }

  /** B9 §3 — the strip's own re-read after a tick/untick, so it and `items`
   * never drift from the server's own state.
   *
   * ⚑ B11 — `loadMedia()` and `getSubjectPageState` are DELIBERATELY
   * INDEPENDENT (Atlas, a live-box walk 2026-09-25), not one `Promise.all`.
   * `items` holds every kind (headshot, reel, credit) — a credit tick
   * touches neither photos nor reels, and yoking the two reads together
   * meant a media-read failure silently kept a credit's own tick from ever
   * rendering as ticked. `loadMedia` reports its own failure on its own;
   * `items` refreshes on its own success regardless of what the media read
   * did. */
  async function loadStrip(id: string): Promise<void> {
    const [, stateResult] = await Promise.allSettled([loadMedia(), getSubjectPageState(tenantId, id)]);
    if (stateResult.status === 'fulfilled') {
      setItems(stateResult.value.items);
    } else {
      // A stale tick is a silent lie about what is on the page — say so.
      const err = stateResult.reason;
      setStripError(
        err instanceof ApiError ? (err.serverMessage ?? err.message) : 'What is on your page could not be re-read.'
      );
    }
  }

  /** B10 §1 — the credits group's own candidate read: every WRAPPED
   * engagement, independent of the media groups' own `loadMedia` call. A
   * read failure here is the group's own, same discipline as `loadMedia`'s
   * own comment — it never blocks the preview or the publish flow. */
  async function loadCreditCandidates(id: string): Promise<void> {
    try {
      setCreditCandidates(await getSubjectPageCreditCandidates(tenantId, id));
      setCreditsError(null);
    } catch (err) {
      setCreditsError(
        err instanceof ApiError ? (err.serverMessage ?? err.message) : 'Your credits could not be read.'
      );
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setPhase('loading');
      setFailure(null);
      setUnavailableMessage(null);
      setAgeAnswerError(null);
      try {
        const performers = await listHouseholdPerformers(tenantId);
        if (cancelled) return;
        const performer = performers[0];
        if (!performer) {
          setUnavailableMessage('There is no performer to build a page for yet.');
          setPhase('unavailable');
          return;
        }
        setSubjectId(performer.id);
        setSubjectName(performer.name);

        const [state, consentText] = await Promise.all([
          getSubjectPageState(tenantId, performer.id),
          getSubjectPageConsentText(tenantId, performer.id),
        ]);
        if (cancelled) return;
        setConsent(consentText);

        // ⚑ THE GATE — see this file's header. `isMinor` covers two
        // server-side cases; `ageUnanswered` (B12) picks out the one this
        // screen can actually DO something about, and `performer.isSelf`
        // says whether THIS session is who may answer it.
        if (consentText.isMinor) {
          if (consentText.ageUnanswered) {
            if (performer.isSelf) {
              setPhase('askAge');
              return;
            }
            // ⚑ S1 (R4) — `recordAgeAttestation` writes to the SIGNED-IN user,
            // so this screen never lets account A answer for performer B. It
            // used to say "Only {name}, signed in as themselves, can answer",
            // which names a second login the one-login model does not have.
            // The fix that works for anyone is the performer's month and year
            // of birth: the server's own age_unknown sentence says so.
            setUnavailableMessage(consentText.ageUnknownMessage);
            setPhase('unavailable');
            return;
          }
          // ⚑ S1 (R5) — answered, but still unknown (an honest "No, under 18"
          // with no month and year on the performer): the server's
          // age_unknown sentence, which names the performer's month and year
          // of birth as the fix. Never the minor sentence: that would leave
          // an honest parent with nothing to do.
          if (consentText.band === 'unknown') {
            setUnavailableMessage(consentText.ageUnknownMessage);
            setPhase('unavailable');
            return;
          }
          // Settled and under 18. The server's own settled-minor sentence, no
          // button: this screen has no publish step for a minor.
          setUnavailableMessage(consentText.minorMessage);
          setPhase('unavailable');
          return;
        }

        if (state.page?.live) {
          setLiveUrl(state.page.url ? sharedPageUrl(state.page.url) : null);
          setPhase('live');
          return;
        }

        // ⚑ B9 §2 — THE ADDRESS, OFFERED BEFORE IT IS ASKED FOR, NEVER SAVED
        // BY LOOKING. B8 PUT the suggestion here so the (now-superseded) JSON
        // preview route would not 409 `slug_required` — which meant opening
        // this screen reserved the account's name at first view (the phone
        // walk's finding #3), bending P3 §1's "first-come at publish". The
        // new preview route (`subjectPagePreviewUrl`, rendered below) needs
        // no address at all, so the field is simply SET from whichever the
        // server already has chosen, or the read-only suggestion when it does
        // not — nothing here writes `subject_pages.slug`. Only
        // `attemptSaveAddress` ("Publish it") does that, exactly as B8 built
        // it (PUT-then-confirm).
        const existingSlug = state.page?.slug ?? null;
        if (existingSlug) {
          setSlugField(existingSlug);
        } else {
          const suggestion = await getSubjectPageSlugSuggestion(tenantId, performer.id);
          if (suggestion) setSlugField(suggestion);
        }

        // B9 §3 — the strip's INITIAL read: `items` is already `state.items`
        // (the one `getSubjectPageState` call above), so only the media list
        // itself needs a fresh read here. B11: `state.appearance` rides the
        // same response — the hero picker's own initial read, also free.
        // A read failure is the strip/reel-group's own, never the whole
        // screen's — see `loadMedia`'s own comment.
        setItems(state.items);
        setAppearance(state.appearance);
        await loadMedia();
        // B10 §1 — the Credits group's own initial read, same non-blocking
        // discipline as the media groups' just above.
        await loadCreditCandidates(performer.id);
        if (cancelled) return;
        setPhase('preview');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setPhase('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, retryNonce]);

  /** B12 — the in-place age question's own answer. The SAME call
   * Onboarding.tsx's setup-age step makes (`recordAgeAttestation`,
   * session-scoped — never `subjectId`: see this file's header on why this
   * phase only ever shows for the session's own performer), then a full
   * `retryNonce` re-read rather than guessing the outcome locally: "Yes"
   * clears `ageUnanswered` and the gate above falls through to the normal
   * preview; "No" leaves it a settled, non-adult refusal (B6 §1 — an
   * is_adult=false attestation stays `age_unknown`, it does not manufacture
   * a minor), and the SAME gate renders that without this function having
   * to know which sentence applies. */
  async function answerAgeInPlace(isAdult: boolean): Promise<void> {
    if (busy) return;
    setBusy(true);
    setAgeAnswerError(null);
    try {
      await recordAgeAttestation(isAdult);
      setBusy(false);
      setRetryNonce((n) => n + 1);
    } catch {
      setBusy(false);
      // Setup's own sentence (AgeQuestion.tsx) — one copy, both screens.
      setAgeAnswerError(AGE_ANSWER_SAVE_FAILED_MESSAGE);
    }
  }

  async function attemptSaveAddress(): Promise<void> {
    if (!subjectId || busy) return;
    const wanted = slugField.trim();
    if (!wanted) {
      setAddrError('Choose an address for the page.');
      return;
    }
    setBusy(true);
    setAddrError(null);
    setTakenLine(null);
    try {
      const saved = await setSubjectPageSlug(tenantId, subjectId, wanted);
      setSlugField(saved.slug);
      setBusy(false);
      setPhase('confirm');
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === 'slug_taken') {
        const candidates = subjectPageCandidates(err);
        const offered = candidates[0] ?? '';
        setSlugField(offered);
        // Blessed verbatim (the mock's Taken screen) — never applied
        // silently: the field stays editable and shows what was offered.
        setTakenLine(
          `${wanted} is someone else's page, so this one is offered instead. Change it to anything that's free.`
        );
        setPhase('taken');
        return;
      }
      // slug_reserved / invalid_slug / anything else — the server's own
      // sentence, shown in place, the field left exactly as typed.
      setAddrError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That address could not be saved.');
    }
  }

  async function submitPublish(): Promise<void> {
    if (!subjectId || !consent || busy) return;
    setBusy(true);
    setPublishError(null);
    try {
      const result = await publishSubjectPage(tenantId, subjectId, {
        typedConfirmation: typedName,
        consentTextVersion: consent.version,
      });
      setLiveUrl(sharedPageUrl(result.url));
      setBusy(false);
      setCopied(false);
      setCopyFailed(false);
      setPhase('live');
    } catch (err) {
      setBusy(false);
      setPublishError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That could not be published.');
    }
  }

  async function doUnpublish(): Promise<void> {
    if (!subjectId || busy) return;
    setBusy(true);
    try {
      const result = await unpublishSubjectPage(tenantId, subjectId);
      setBusy(false);
      setLiveUrl(null);
      setUnpublishReceipt(result.receipt);
      setPhase('preview');
    } catch (err) {
      setBusy(false);
      setAddrError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That could not be taken down.');
    }
  }

  /** B9 §3 — tick/untick one headshot on the "On the page" strip. Ticking
   * appends at the end of the account's chosen order (position = however
   * many headshot items already exist), so the FIRST one ever ticked lands
   * at position 0 — `pickHero` (publicSubjectPage.ts) picks `headshots[0]`,
   * so that first tick is what becomes the hero, exactly as the computed
   * default already prefers a headshot hero whenever one is selected
   * (`computeDefaultThemeAndHero`, core/publicPage/pages.ts). */
  async function toggleStripItem(media: RecordRow): Promise<void> {
    if (!subjectId || stripBusyId) return;
    const existing = items.find((i) => i.itemKind === 'headshot' && i.recordId === media.id);
    setStripBusyId(media.id);
    setStripError(null);
    try {
      if (existing) {
        await removeSubjectPageItem(tenantId, subjectId, media.id);
      } else {
        const headshotCount = items.filter((i) => i.itemKind === 'headshot').length;
        await addSubjectPageItem(tenantId, subjectId, {
          itemKind: 'headshot',
          recordId: media.id,
          position: headshotCount,
        });
      }
      await loadStrip(subjectId);
      setPreviewNonce((n) => n + 1);
    } catch (err) {
      setStripError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That photo could not be changed.');
    } finally {
      setStripBusyId(null);
    }
  }

  /** B10 §1 — tick/untick one credit in the "Credits" group. Same shape as
   * `toggleStripItem`: ticking POSTs `itemKind: 'credit'`, unticking DELETEs
   * by `recordId` (the shared items door), and either reloads `items` and
   * bumps `previewNonce` so the preview reflects the change. Default is
   * unticked — nothing here is added by opening the screen (B9 §2's rule,
   * carried over). */
  async function toggleCreditItem(candidate: SubjectPageCreditCandidateApi): Promise<void> {
    if (!subjectId || creditBusyId) return;
    const existing = items.find((i) => i.itemKind === 'credit' && i.recordId === candidate.recordId);
    setCreditBusyId(candidate.recordId);
    setCreditsError(null);
    try {
      if (existing) {
        await removeSubjectPageItem(tenantId, subjectId, candidate.recordId);
      } else {
        await addSubjectPageItem(tenantId, subjectId, { itemKind: 'credit', recordId: candidate.recordId });
      }
      await loadStrip(subjectId);
      setPreviewNonce((n) => n + 1);
    } catch (err) {
      setCreditsError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That credit could not be changed.');
    } finally {
      setCreditBusyId(null);
    }
  }

  /** B11 §3 — tick/untick one reel in the "Reels" group. Same shape as
   * `toggleStripItem`, `itemKind: 'reel'`. A reel ticked here is NOT the
   * hero by itself — that is the separate choice `chooseHero` below writes
   * — but the first reel ever ticked is what a later "Reel first" pick
   * would promote (`pickHero`'s own `reels[0]` rule, same precedent
   * `toggleStripItem`'s own comment names for headshots). */
  async function toggleReelItem(media: RecordRow): Promise<void> {
    if (!subjectId || reelBusyId) return;
    const existing = items.find((i) => i.itemKind === 'reel' && i.recordId === media.id);
    setReelBusyId(media.id);
    setStripError(null);
    try {
      if (existing) {
        await removeSubjectPageItem(tenantId, subjectId, media.id);
      } else {
        const reelCount = items.filter((i) => i.itemKind === 'reel').length;
        await addSubjectPageItem(tenantId, subjectId, {
          itemKind: 'reel',
          recordId: media.id,
          position: reelCount,
        });
      }
      await loadStrip(subjectId);
      setPreviewNonce((n) => n + 1);
    } catch (err) {
      setStripError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That reel could not be changed.');
    } finally {
      setReelBusyId(null);
    }
  }

  /** B11 §3 — "Headshot first" / "Reel first". Writes `hero_kind` through
   * the existing `PUT .../page/appearance`.
   *
   * ⚑ CHOOSING THE REEL WHILE THE THEME IS THE INDEX (C) MOVES IT TO THE
   * TITLE PAGE (A), IN THE SAME WRITE. Migration 0109's own header states
   * the rule in exactly these terms — "promote the reel to hero and C has
   * become A" — and the appearance route validates the COMBINED result, so
   * sending `heroKind: 'reel'` alone while the stored theme is still the
   * index would be refused with `index_theme_cannot_take_reel_hero`
   * (`setSubjectPageAppearance`, `@vowric/core`). Rather than let the
   * artist's very next tap land on that refusal and have to guess what to
   * do about it, the SAME tap that promotes the reel above the fold also
   * moves the theme — a direct, named consequence of the button just
   * pressed, never a silent side effect of a later one. "Headshot first"
   * never needs this: every theme already takes a headshot hero. */
  async function chooseHero(heroKind: SubjectPageHeroKind): Promise<void> {
    if (!subjectId || appearanceBusy) return;
    setAppearanceBusy(true);
    setAppearanceError(null);
    try {
      const args: { theme?: 'title_page' | 'contact_sheet' | 'index'; heroKind: SubjectPageHeroKind } = { heroKind };
      if (heroKind === 'reel' && appearance?.theme === 'index') {
        args.theme = 'title_page';
      }
      const result = await setSubjectPageAppearance(tenantId, subjectId, args);
      setAppearance(result.appearance);
      setPreviewNonce((n) => n + 1);
    } catch (err) {
      setAppearanceError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That look could not be changed.');
    } finally {
      setAppearanceBusy(false);
    }
  }

  async function copyLink(): Promise<void> {
    if (!liveUrl) return;
    try {
      await navigator.clipboard.writeText(liveUrl);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  }

  const onwardLabel = mode === 'onboarding' ? 'Go to Today' : 'Done';
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  if (phase === 'loading') {
    return (
      <div class="yp" data-testid="yp-loading">
        <div class="yp__eyebrow">Your page</div>
        <div class="yp__say">One moment…</div>
      </div>
    );
  }

  if (phase === 'error' && failure !== null) {
    return (
      <div class="yp" data-testid="yp-error">
        <div class="yp__eyebrow">Your page</div>
        <div class="yp__say">
          {failure.headline}
          <br />
          <span class="yp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="yp__textlink" onClick={() => setRetryNonce((n) => n + 1)}>
            Read this again
          </button>
        ) : null}
        <button type="button" class="yp__textlink" onClick={onDone}>
          {onwardLabel}
        </button>
      </div>
    );
  }

  if (phase === 'unavailable') {
    return (
      <div class="yp" data-testid="yp-unavailable">
        <div class="yp__eyebrow">Your page</div>
        <div class="yp__say-soft" data-testid="yp-unavailable-message">
          {unavailableMessage}
        </div>
        <div class="yp__grow" />
        <button type="button" class="yp__btn" onClick={onDone}>
          {onwardLabel}
        </button>
      </div>
    );
  }

  // B12 — the age question, asked in place, using the SAME shared component
  // and words Onboarding.tsx's own setup-age step uses.
  if (phase === 'askAge') {
    return (
      <div class="yp" data-testid="yp-ask-age">
        <div class="yp__eyebrow">Your page</div>
        <AgeQuestion
          performerName={subjectName}
          busy={busy}
          error={ageAnswerError}
          onAnswer={(isAdult) => void answerAgeInPlace(isAdult)}
        />
      </div>
    );
  }

  if (phase === 'notYet') {
    return (
      <div class="yp" data-testid="yp-notyet">
        <div class="yp__eyebrow">Your page</div>
        <h1 class="yp__heading">Kept private for now.</h1>
        <p class="yp__body">
          Nobody can see it. It stays under More → Your page, ready to publish when you are.
        </p>
        <div class="yp__grow" />
        <button type="button" class="yp__btn" onClick={onDone} data-testid="yp-notyet-onward">
          {onwardLabel}
        </button>
      </div>
    );
  }

  if (phase === 'live') {
    return (
      <div class="yp" data-testid="yp-live">
        <div class="yp__eyebrow">Live</div>
        <h1 class="yp__heading">Your page is up.</h1>
        <div class="yp__livecard">
          <div class="yp__liveurl" data-testid="yp-live-url">
            {liveUrl?.replace(/^https:\/\//, '')}
          </div>
          <div class="yp__liverow">
            <button type="button" class="yp__btn yp__btn--inline" onClick={() => void copyLink()}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
            {canShare ? (
              <button
                type="button"
                class="yp__btn yp__btn--outline yp__btn--inline"
                onClick={() => {
                  void navigator.share({ url: liveUrl ?? undefined, title: subjectName }).catch(() => {
                    // A cancelled share (AbortError, the overwhelmingly common
                    // case — the person closed the OS share sheet) and a real
                    // failure both land here, and the share sheet itself is
                    // where any feedback belongs: it is native chrome this
                    // screen never drew and has nothing truer to say about it.
                  });
                }}
              >
                Share…
              </button>
            ) : null}
          </div>
          {copyFailed ? <div class="yp__error">Couldn't copy — select the address above by hand.</div> : null}
        </div>
        <p class="yp__body">
          Put it in an email signature, on a submission, or in your bio. Credits you approve from now on can be
          added to it.
        </p>
        <div class="yp__grow" />
        {mode === 'more' ? (
          <button
            type="button"
            class="yp__textlink"
            data-testid="yp-unpublish"
            disabled={busy}
            onClick={() => void doUnpublish()}
          >
            Take this page down
          </button>
        ) : null}
        <button type="button" class="yp__btn" onClick={onDone} data-testid="yp-live-onward">
          {onwardLabel}
        </button>
      </div>
    );
  }

  if (phase === 'confirm' && consent !== null) {
    const disabled = busy || typedName.trim() !== subjectName.trim();
    return (
      <div class="yp" data-testid="yp-confirm">
        <div class="yp__eyebrow">Before it goes live</div>
        <h1 class="yp__heading">Anyone with the link can see this page.</h1>
        <div class="yp__consent" data-testid="yp-consent-paragraphs">
          {consent.paragraphs.map((p, i) => (
            <p class="yp__body" key={i}>
              {p}
            </p>
          ))}
        </div>
        <div class="yp__field">
          <label class="yp__label" for="yp-typed-name">
            {consent.typePrompt}
          </label>
          <input
            id="yp-typed-name"
            class="yp__input"
            type="text"
            value={typedName}
            onInput={(e) => setTypedName((e.target as HTMLInputElement).value)}
          />
        </div>
        {publishError ? <div class="yp__error">{publishError}</div> : null}
        <div class="yp__grow" />
        <button type="button" class="yp__btn" disabled={disabled} onClick={() => void submitPublish()}>
          {`Publish ${slugField}`}
        </button>
        <button type="button" class="yp__textlink" onClick={() => setPhase(takenLine ? 'taken' : 'preview')}>
          ‹ Back to the preview
        </button>
      </div>
    );
  }

  // preview / taken — the same layout; `taken` adds the offered-address line
  // and never silently replaces what was typed.
  return (
    <div class="yp" data-testid={phase === 'taken' ? 'yp-taken' : 'yp-preview'}>
      <div class="yp__eyebrow">Your page</div>
      <h1 class="yp__heading">This is what casting sees.</h1>
      {unpublishReceipt ? <div class="yp__receipt">{unpublishReceipt}</div> : null}
      <div class="yp__frame">
        {/* B9 §2 — reached here only once `subjectId` is set (every path into
            this phase sets it first), and the preview needs no address at
            all — so, unlike B8, there is no "waiting for an address" empty
            state to render instead. */}
        <iframe
          key={previewNonce}
          class="yp__frame-iframe"
          src={`${subjectPagePreviewUrl(tenantId, subjectId!)}&v=${previewNonce}`}
          sandbox="allow-same-origin"
          title="Your page preview"
          data-testid="yp-preview-frame"
        />
      </div>
      {/* B11 — a failed media read is NOT absence (same rule the Credits
          group already follows, below): it shows the server's own sentence
          even though there is nothing to list, rather than the section
          simply not being there. Shown once, ahead of BOTH media groups —
          one `aa_media` read feeds them both (`loadMedia`), so one failure
          is one sentence, never repeated per group. */}
      {stripError ? <div class="yp__error" data-testid="yp-media-error">{stripError}</div> : null}
      {photos !== null && photos.length > 0 ? (
        <div class="yp__field">
          <label class="yp__label">On the page</label>
          <div class="yp__strip" data-testid="yp-strip">
            {photos.map((m) => {
              const on = items.some((i) => i.itemKind === 'headshot' && i.recordId === m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  class={`yp__stripitem${on ? ' yp__stripitem--on' : ''}`}
                  aria-pressed={on}
                  disabled={stripBusyId === m.id}
                  data-testid={`yp-strip-${m.id}`}
                  onClick={() => void toggleStripItem(m)}
                >
                  <RecordImage tenantId={tenantId} recordId={m.id} fieldName="original_file_ref" alt="" />
                  <span class="yp__stripmark" aria-hidden="true">
                    {on ? '✓' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {/* B11 §3 — "Reels": the same tick door, `itemKind: 'reel'`, shown by
          its cover still — a plain plate when one is absent (P2's rule,
          never a black rectangle), extended here to this curation screen.
          Absent with zero reels (P1: absence is absent) — the failed-read
          case is covered by the shared `stripError` line above, not here. */}
      {reels !== null && reels.length > 0 ? (
        <div class="yp__field">
          <label class="yp__label">Reels</label>
          <div class="yp__strip" data-testid="yp-reelstrip">
            {reels.map((m) => {
              const on = items.some((i) => i.itemKind === 'reel' && i.recordId === m.id);
              const coverRef = m.fields['cover_frame_ref'];
              const hasCover = typeof coverRef === 'string' && coverRef.length > 0;
              return (
                <button
                  key={m.id}
                  type="button"
                  class={`yp__stripitem${on ? ' yp__stripitem--on' : ''}`}
                  aria-pressed={on}
                  disabled={reelBusyId === m.id}
                  data-testid={`yp-reel-${m.id}`}
                  onClick={() => void toggleReelItem(m)}
                >
                  {hasCover ? (
                    <RecordImage tenantId={tenantId} recordId={m.id} fieldName="cover_frame_ref" alt="" />
                  ) : (
                    <div class="yp__stripplate" aria-hidden="true" data-testid={`yp-reel-plate-${m.id}`}>
                      Reel
                    </div>
                  )}
                  <span class="yp__stripmark" aria-hidden="true">
                    {on ? '✓' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {/* B11 §3 — "Headshot first" / "Reel first", absent with no reel
          ticked (P1: no dead option) — never disabled, never shown. */}
      {items.some((i) => i.itemKind === 'reel') ? (
        <div class="yp__field">
          <label class="yp__label">Lead with</label>
          <div class="yp__herochoice" data-testid="yp-herochoice">
            <button
              type="button"
              class={`yp__herobtn${appearance?.heroKind === 'headshot' ? ' yp__herobtn--on' : ''}`}
              aria-pressed={appearance?.heroKind === 'headshot'}
              disabled={appearanceBusy}
              data-testid="yp-hero-headshot"
              onClick={() => void chooseHero('headshot')}
            >
              Headshot first
            </button>
            <button
              type="button"
              class={`yp__herobtn${appearance?.heroKind === 'reel' ? ' yp__herobtn--on' : ''}`}
              aria-pressed={appearance?.heroKind === 'reel'}
              disabled={appearanceBusy}
              data-testid="yp-hero-reel"
              onClick={() => void chooseHero('reel')}
            >
              Reel first
            </button>
          </div>
          {appearanceError ? <div class="yp__error">{appearanceError}</div> : null}
        </div>
      ) : null}
      {/* B10 §1 — absence is absent: no heading, no empty-state sentence,
          with zero wrapped credits (P1's own rule, carried over from the
          strip above). A failed READ is not absence, though: the group
          still appears to carry its own error, or the failure is silent. */}
      {(creditCandidates !== null && creditCandidates.length > 0) || creditsError ? (
        <div class="yp__field">
          <label class="yp__label">Credits</label>
          <div class="yp__credits" data-testid="yp-credits">
            {(creditCandidates ?? []).map((c) => {
              const on = items.some((i) => i.itemKind === 'credit' && i.recordId === c.recordId);
              return (
                <button
                  key={c.recordId}
                  type="button"
                  class={`yp__creditrow${on ? ' yp__creditrow--on' : ''}`}
                  aria-pressed={on}
                  disabled={creditBusyId === c.recordId}
                  data-testid={`yp-credit-${c.recordId}`}
                  onClick={() => void toggleCreditItem(c)}
                >
                  <span class="yp__creditmark" aria-hidden="true">
                    {on ? '✓' : ''}
                  </span>
                  <span class="yp__credit-production">{c.production}</span>
                  <span class="yp__credit-role">{c.role}</span>
                  {c.year !== null ? <span class="yp__credit-year">{c.year}</span> : null}
                </button>
              );
            })}
          </div>
          {creditsError ? <div class="yp__error">{creditsError}</div> : null}
        </div>
      ) : null}
      <div class="yp__field">
        <label class="yp__label" for="yp-addr">
          The page's address
        </label>
        <div class="yp__addr">
          <span class="yp__addr-fix">{PAGE_ADDRESS_PREFIX}</span>
          <input
            id="yp-addr"
            class="yp__input yp__input--addr"
            type="text"
            value={slugField}
            onInput={(e) => setSlugField((e.target as HTMLInputElement).value)}
          />
        </div>
        {takenLine ? <p class="yp__body">{takenLine}</p> : null}
        {addrError ? <div class="yp__error">{addrError}</div> : null}
      </div>
      <div class="yp__grow" />
      <button type="button" class="yp__btn" disabled={busy} onClick={() => void attemptSaveAddress()}>
        Publish it
      </button>
      <button type="button" class="yp__textlink" onClick={() => setPhase('notYet')}>
        Not yet
      </button>
    </div>
  );
}
